import { useCallback, useRef, useState } from 'react'
import type { CrmChatProviderKey } from '@/api/types/crm/crmLeadFunnels.types'
import type { AxiosResponse } from 'axios'
import type { ResponseWrapper } from '@/api/types/base.types'
import { crmIntegrationChatApi } from '../api/crm-integration-chat.api'
import type { CrmIntegrationOutgoingAttachment } from '../types/crm-integration-chat.types'
import type { CrmIntegrationPendingMessage } from '../types/crm-integration-message.types'
import {
  CRM_INTEGRATION_SEND_FAILURE_MESSAGE,
  getCrmIntegrationApiErrorText,
  getCrmIntegrationSendErrorMessage,
} from '../lib/crmIntegrationApiErrors'
import {
  createCrmIntegrationPendingImagePreviewUrl,
  createCrmIntegrationPendingVideoPreviewUrl,
  revokeCrmIntegrationPendingPreviewUrl,
} from '../lib/crmIntegrationPendingMediaPreview'
import { isCrmIntegrationOutgoingTextAttachmentXor } from '../lib/crmChatProviderKey'
import {
  uploadCrmIntegrationOutgoingAttachment,
  isCrmIntegrationOutgoingImageFile,
  isCrmIntegrationOutgoingVideoFile,
  validateCrmIntegrationOutgoingAttachment,
} from '../lib/crmIntegrationOutgoingAttachment'
import { validateWazzupOutgoingAttachment } from '../lib/crmIntegrationWazzupOutgoingAttachment'
import {
  CRM_VOICE_UPLOAD_DISPLAY_NAME,
  isCrmIntegrationVoiceFileWithinSizeLimit,
  validateCrmIntegrationOutgoingVoiceFile,
} from '../lib/crmIntegrationVoiceFile'
import {
  isCrmIntegrationSendAxiosError,
  resolveCrmIntegrationSendResponse,
} from '../lib/parseCrmIntegrationSendResponse'

const createPendingClientId = (): string =>
  `crm-pending-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

const revokeRemovedPendingPreviews = (
  previous: CrmIntegrationPendingMessage[],
  next: CrmIntegrationPendingMessage[],
): void => {
  const nextClientIds = new Set(next.map((item) => item.clientId))

  previous.forEach((item) => {
    if (!nextClientIds.has(item.clientId)) {
      revokeCrmIntegrationPendingPreviewUrl(item.pendingImagePreviewUrl)
      revokeCrmIntegrationPendingPreviewUrl(item.pendingVideoPreviewUrl)
      revokeCrmIntegrationPendingPreviewUrl(item.pendingVoicePreviewUrl)
    }
  })
}

export type UseCrmIntegrationSendMessageParams = {
  conversationId: number | null
  provider: CrmChatProviderKey
  managerUserId: number | undefined
  loadMessages: (loadOptions?: { silent?: boolean }) => Promise<void>
  /** Wazzup: тип канала для валидации вложений. */
  wazzupChannelType?: string | null
}

export type UseCrmIntegrationSendMessageResult = {
  pendingMessages: CrmIntegrationPendingMessage[]
  setPendingMessages: (
    updater:
      | CrmIntegrationPendingMessage[]
      | ((prev: CrmIntegrationPendingMessage[]) => CrmIntegrationPendingMessage[]),
  ) => void
  isSending: boolean
  sendMessage: (text: string, file?: File | null) => Promise<void>
  sendVoiceMessage: (
    file: File,
    previewUrl: string,
    durationSec?: number | null,
  ) => Promise<void>
  retrySendMessage: (clientId: string) => Promise<void>
  resetSendState: () => void
  confirmOutgoingDelivery: (messageId: number) => void
  handleOutgoingDeliveryFailed: (messageId: number, error: string) => void
}

export const useCrmIntegrationSendMessage = ({
  conversationId,
  provider,
  managerUserId,
  loadMessages,
  wazzupChannelType = null,
}: UseCrmIntegrationSendMessageParams): UseCrmIntegrationSendMessageResult => {
  const [pendingMessages, setPendingMessagesState] = useState<
    CrmIntegrationPendingMessage[]
  >([])
  const [isSending, setIsSending] = useState(false)

  const conversationIdRef = useRef(conversationId)
  conversationIdRef.current = conversationId

  const providerRef = useRef(provider)
  providerRef.current = provider

  const wazzupChannelTypeRef = useRef(wazzupChannelType)
  wazzupChannelTypeRef.current = wazzupChannelType

  const managerUserIdRef = useRef(managerUserId)
  managerUserIdRef.current = managerUserId

  const loadMessagesRef = useRef(loadMessages)
  loadMessagesRef.current = loadMessages

  const pendingMessagesRef = useRef(pendingMessages)
  pendingMessagesRef.current = pendingMessages

  const setPendingMessages = useCallback(
    (
      updater:
        | CrmIntegrationPendingMessage[]
        | ((
            prev: CrmIntegrationPendingMessage[],
          ) => CrmIntegrationPendingMessage[]),
    ) => {
      setPendingMessagesState((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater
        revokeRemovedPendingPreviews(prev, next)
        return next
      })
    },
    [],
  )

  const markPendingError = useCallback((clientId: string, errorText: string) => {
    setPendingMessages((prev) =>
      prev.map((item) =>
        item.clientId === clientId
          ? {
              ...item,
              status: 'error' as const,
              errorText: errorText || CRM_INTEGRATION_SEND_FAILURE_MESSAGE,
            }
          : item,
      ),
    )
  }, [setPendingMessages])

  const removePendingForDeliveredMessage = useCallback(
    (messageId: number) => {
      setPendingMessages((prev) => {
        const hasConfirmed = prev.some(
          (item) => item.confirmedServerMessageId === messageId,
        )
        if (hasConfirmed) {
          return prev.filter((item) => item.confirmedServerMessageId !== messageId)
        }

        const currentConversationId = conversationIdRef.current
        const looseMatches = prev.filter(
          (item) =>
            item.conversationId === currentConversationId &&
            (item.status === 'sending' || item.status === 'error'),
        )
        if (looseMatches.length === 1) {
          return prev.filter((item) => item.clientId !== looseMatches[0].clientId)
        }

        return prev
      })
    },
    [setPendingMessages],
  )

  const confirmOutgoingDelivery = useCallback(
    (messageId: number) => {
      if (!Number.isFinite(messageId) || messageId <= 0) {
        return
      }

      removePendingForDeliveredMessage(messageId)
      void loadMessagesRef.current({ silent: true })
    },
    [removePendingForDeliveredMessage],
  )

  const handleOutgoingDeliveryFailed = useCallback(
    (messageId: number, error: string) => {
      if (!Number.isFinite(messageId) || messageId <= 0) {
        return
      }

      const errorText = error.trim() || CRM_INTEGRATION_SEND_FAILURE_MESSAGE

      setPendingMessages((prev) => {
        const hasConfirmed = prev.some(
          (item) => item.confirmedServerMessageId === messageId,
        )
        if (hasConfirmed) {
          return prev.map((item) =>
            item.confirmedServerMessageId === messageId
              ? {
                  ...item,
                  status: 'error' as const,
                  errorText,
                }
              : item,
          )
        }

        const currentConversationId = conversationIdRef.current
        const looseMatches = prev.filter(
          (item) =>
            item.conversationId === currentConversationId &&
            item.status === 'sending',
        )
        if (looseMatches.length === 1) {
          return prev.map((item) =>
            item.clientId === looseMatches[0].clientId
              ? {
                  ...item,
                  status: 'error' as const,
                  errorText,
                  confirmedServerMessageId: messageId,
                }
              : item,
          )
        }

        return prev
      })

      void loadMessagesRef.current({ silent: true })
    },
    [setPendingMessages],
  )

  const executeSend = useCallback(
    async (
      text: string,
      file: File | null | undefined,
      existingClientId?: string,
      existingAttachments?: CrmIntegrationOutgoingAttachment[],
    ) => {
      const trimmed = text.trim()
      const hasFile = Boolean(file)
      const hasStoredAttachments = (existingAttachments?.length ?? 0) > 0

      if (!trimmed && !hasFile && !hasStoredAttachments) {
        return
      }

      const currentConversationId = conversationIdRef.current
      if (currentConversationId == null) {
        return
      }

      const clientId = existingClientId ?? createPendingClientId()

      if (!existingClientId) {
        setPendingMessages((prev) => [
          ...prev,
          {
            clientId,
            conversationId: currentConversationId,
            text: trimmed,
            createdAt: new Date().toISOString(),
            status: 'sending',
            attachmentFileName: file?.name ?? null,
            pendingImagePreviewUrl:
              file && isCrmIntegrationOutgoingImageFile(file)
                ? createCrmIntegrationPendingImagePreviewUrl(file)
                : undefined,
            pendingVideoPreviewUrl:
              file && isCrmIntegrationOutgoingVideoFile(file)
                ? createCrmIntegrationPendingVideoPreviewUrl(file)
                : undefined,
          },
        ])
      } else {
        setPendingMessages((prev) =>
          prev.map((item) =>
            item.clientId === clientId
              ? {
                  ...item,
                  status: 'sending' as const,
                  errorText: null,
                  confirmedServerMessageId: undefined,
                }
              : item,
          ),
        )
      }

      setIsSending(true)

      try {
        let attachments = existingAttachments

        if (file) {
          if (providerRef.current === 'wazzup') {
            const wazzupError = validateWazzupOutgoingAttachment(
              file,
              wazzupChannelTypeRef.current,
            )
            if (wazzupError) {
              markPendingError(clientId, wazzupError)
              return
            }
          } else {
            const attachmentError = validateCrmIntegrationOutgoingAttachment(
              file,
              providerRef.current,
            )
            if (attachmentError) {
              markPendingError(clientId, attachmentError)
              return
            }
          }

          const currentManagerUserId = managerUserIdRef.current
          if (currentManagerUserId == null) {
            markPendingError(
              clientId,
              'Не удалось определить пользователя для загрузки файлов',
            )
            return
          }

          const outgoingAttachment = await uploadCrmIntegrationOutgoingAttachment(
            file,
            currentManagerUserId,
            providerRef.current,
          )
          attachments = [outgoingAttachment]

          setPendingMessages((prev) =>
            prev.map((item) =>
              item.clientId === clientId
                ? { ...item, uploadedOutgoingAttachments: attachments }
                : item,
            ),
          )
        }

        // Wazzup: text XOR attachment — если оба есть, в этом запросе уходит только вложение.
        const sendText =
          isCrmIntegrationOutgoingTextAttachmentXor(providerRef.current) &&
          (attachments?.length ?? 0) > 0
            ? ''
            : trimmed

        const res = await crmIntegrationChatApi.sendIntegrationMessage({
          conversation_id: currentConversationId,
          text: sendText,
          attachments,
        })

        const parsed = resolveCrmIntegrationSendResponse(res)
        if (parsed == null) {
          const errorText = isCrmIntegrationSendAxiosError(res)
            ? getCrmIntegrationApiErrorText(
                res as AxiosResponse<ResponseWrapper<unknown>>,
                CRM_INTEGRATION_SEND_FAILURE_MESSAGE,
              )
            : CRM_INTEGRATION_SEND_FAILURE_MESSAGE
          markPendingError(clientId, errorText)
          return
        }

        setPendingMessages((prev) =>
          prev.map((item) =>
            item.clientId === clientId
              ? {
                  ...item,
                  text: sendText,
                  status: 'sending' as const,
                  errorText: null,
                  confirmedServerMessageId: parsed.message_id,
                }
              : item,
          ),
        )

        try {
          await loadMessagesRef.current({ silent: true })
        } catch {
          // финальное подтверждение — WS integration_message_delivered
        }
      } catch (sendError) {
        markPendingError(clientId, getCrmIntegrationSendErrorMessage(sendError))
      } finally {
        setIsSending(false)
      }
    },
    [markPendingError, setPendingMessages],
  )

  const sendMessage = useCallback(
    async (text: string, file?: File | null) => {
      const trimmed = text.trim()
      // Wazzup: файл и текст — два отдельных send (сначала вложение, потом caption).
      if (
        isCrmIntegrationOutgoingTextAttachmentXor(providerRef.current) &&
        file &&
        trimmed
      ) {
        await executeSend('', file)
        await executeSend(trimmed, null)
        return
      }

      await executeSend(text, file)
    },
    [executeSend],
  )

  const sendVoiceMessage = useCallback(
    async (file: File, previewUrl: string, durationSec?: number | null) => {
      if (
        !isCrmIntegrationVoiceFileWithinSizeLimit(
          file,
          providerRef.current,
          wazzupChannelTypeRef.current,
        )
      ) {
        const validationError = validateCrmIntegrationOutgoingVoiceFile(
          file,
          providerRef.current,
          wazzupChannelTypeRef.current,
        )
        throw new Error(
          validationError ??
            'Голосовое сообщение превышает допустимый размер',
        )
      }

      const currentConversationId = conversationIdRef.current
      if (currentConversationId == null) {
        return
      }

      const clientId = createPendingClientId()

      setPendingMessages((prev) => [
        ...prev,
        {
          clientId,
          conversationId: currentConversationId,
          text: '',
          createdAt: new Date().toISOString(),
          status: 'sending',
          isVoiceMessage: true,
          pendingVoicePreviewUrl: previewUrl,
          pendingVoiceDurationSec: durationSec ?? null,
          attachmentFileName: file.name,
        },
      ])

      setIsSending(true)

      try {
        const currentManagerUserId = managerUserIdRef.current
        if (currentManagerUserId == null) {
          markPendingError(
            clientId,
            'Не удалось определить пользователя для загрузки файлов',
          )
          return
        }

        const outgoingAttachment = await uploadCrmIntegrationOutgoingAttachment(
          file,
          currentManagerUserId,
          providerRef.current,
          {
            displayName: CRM_VOICE_UPLOAD_DISPLAY_NAME,
            forceVoice: true,
            duration: durationSec ?? undefined,
          },
        )

        setPendingMessages((prev) =>
          prev.map((item) =>
            item.clientId === clientId
              ? {
                  ...item,
                  uploadedOutgoingAttachments: [outgoingAttachment],
                }
              : item,
          ),
        )

        const res = await crmIntegrationChatApi.sendIntegrationMessage({
          conversation_id: currentConversationId,
          text: '',
          attachments: [outgoingAttachment],
        })

        const parsed = resolveCrmIntegrationSendResponse(res)
        if (parsed == null) {
          const errorText = isCrmIntegrationSendAxiosError(res)
            ? getCrmIntegrationApiErrorText(
                res as AxiosResponse<ResponseWrapper<unknown>>,
                CRM_INTEGRATION_SEND_FAILURE_MESSAGE,
              )
            : CRM_INTEGRATION_SEND_FAILURE_MESSAGE
          markPendingError(clientId, errorText)
          return
        }

        setPendingMessages((prev) =>
          prev.map((item) =>
            item.clientId === clientId
              ? {
                  ...item,
                  status: 'sending' as const,
                  errorText: null,
                  confirmedServerMessageId: parsed.message_id,
                }
              : item,
          ),
        )

        try {
          await loadMessagesRef.current({ silent: true })
        } catch {
          // финальное подтверждение — WS integration_message_delivered
        }
      } catch (sendError) {
        markPendingError(clientId, getCrmIntegrationSendErrorMessage(sendError))
        throw sendError
      } finally {
        setIsSending(false)
      }
    },
    [markPendingError, setPendingMessages],
  )

  const retrySendMessage = useCallback(
    async (clientId: string) => {
      const pending = pendingMessagesRef.current.find(
        (item) => item.clientId === clientId,
      )
      if (!pending) {
        return
      }

      if (
        pending.isVoiceMessage &&
        !(pending.uploadedOutgoingAttachments?.length ?? 0)
      ) {
        markPendingError(
          clientId,
          'Повторите отправку: запишите голосовое сообщение заново',
        )
        return
      }

      if (
        pending.attachmentFileName &&
        !pending.isVoiceMessage &&
        !(pending.uploadedOutgoingAttachments?.length ?? 0)
      ) {
        markPendingError(
          clientId,
          'Повторите отправку: прикрепите изображение заново',
        )
        return
      }

      const pendingText = pending.text.trim()
      const pendingAttachments = pending.uploadedOutgoingAttachments

      // Wazzup: если в pending остались и текст, и вложение — два send.
      if (
        isCrmIntegrationOutgoingTextAttachmentXor(providerRef.current) &&
        (pendingAttachments?.length ?? 0) > 0 &&
        pendingText
      ) {
        await executeSend('', null, clientId, pendingAttachments)
        await executeSend(pendingText, null)
        return
      }

      await executeSend(
        pending.text,
        null,
        clientId,
        pendingAttachments,
      )
    },
    [executeSend, markPendingError],
  )

  const resetSendState = useCallback(() => {
    setPendingMessages((prev) => {
      prev.forEach((item) => {
        revokeCrmIntegrationPendingPreviewUrl(item.pendingImagePreviewUrl)
        revokeCrmIntegrationPendingPreviewUrl(item.pendingVideoPreviewUrl)
        revokeCrmIntegrationPendingPreviewUrl(item.pendingVoicePreviewUrl)
      })
      return []
    })
    setIsSending(false)
  }, [setPendingMessages])

  return {
    pendingMessages,
    setPendingMessages,
    isSending,
    sendMessage,
    sendVoiceMessage,
    retrySendMessage,
    resetSendState,
    confirmOutgoingDelivery,
    handleOutgoingDeliveryFailed,
  }
}
